import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Users, Building2, User, FileText, Phone, Mail, MapPin, Shield } from 'lucide-react'
import { kontakteAPI, type KontaktDetail as KontaktDetailType, type KontaktTag } from '@/utils/api'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { KontaktHeader } from './KontaktHeader'
import { StammdatenTab } from './StammdatenTab'
import { KontaktdatenTab } from './KontaktdatenTab'
import { AktenTab } from './AktenTab'
import { VersicherungenTab } from './VersicherungenTab'

interface KontaktDetailProps {
  kontaktId: number
}

export function KontaktDetail({ kontaktId }: KontaktDetailProps) {
  const navigate = useNavigate()
  const [kontakt, setKontakt] = useState<KontaktDetailType | null>(null)
  const [allTags, setAllTags] = useState<KontaktTag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('stammdaten')

  // Track edited fields separately from original kontakt
  const [editedFields, setEditedFields] = useState<Partial<KontaktDetailType>>({})

  const hasChanges = Object.keys(editedFields).length > 0

  const fetchKontakt = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [kontaktData, tagsData] = await Promise.all([
        kontakteAPI.getKontaktById(kontaktId),
        kontakteAPI.getTags(),
      ])
      setKontakt(kontaktData)
      setAllTags(tagsData)
      setEditedFields({})
    } catch (err) {
      console.error('Failed to fetch kontakt:', err)
      setError('Kontakt konnte nicht geladen werden.')
    } finally {
      setIsLoading(false)
    }
  }, [kontaktId])

  useEffect(() => {
    fetchKontakt()
  }, [fetchKontakt])

  const handleFieldChange = (field: keyof KontaktDetailType, value: any) => {
    setEditedFields(prev => {
      // If value is same as original, remove from edited fields
      if (kontakt && kontakt[field] === value) {
        const { [field]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [field]: value }
    })
  }

  const handleSave = async () => {
    if (!kontakt || !hasChanges) return
    setIsSaving(true)
    try {
      // Handle tag_ids specially - convert tags array to tag_ids
      const updateData: Record<string, any> = { ...editedFields }
      if (editedFields.tags) {
        updateData.tag_ids = (editedFields.tags as KontaktTag[]).map(t => t.id)
        delete updateData.tags
      }

      await kontakteAPI.updateKontakt(kontaktId, updateData)
      toast.success('Kontakt erfolgreich gespeichert')
      await fetchKontakt()
    } catch (err) {
      console.error('Failed to save kontakt:', err)
      toast.error('Kontakt konnte nicht gespeichert werden.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!kontakt) return
    setIsDeleting(true)
    try {
      await kontakteAPI.deleteKontakt(kontaktId)
      toast.success('Kontakt erfolgreich gelöscht')
      navigate({ to: '/lawyer/kontakte' })
    } catch (err) {
      console.error('Failed to delete kontakt:', err)
      toast.error('Kontakt konnte nicht gelöscht werden.')
      setIsDeleting(false)
    }
  }

  const handleBack = () => {
    navigate({ to: '/lawyer/kontakte' })
  }

  // Get current value (edited or original)
  const getValue = <K extends keyof KontaktDetailType>(field: K): KontaktDetailType[K] | undefined => {
    if (field in editedFields) {
      return editedFields[field] as KontaktDetailType[K]
    }
    return kontakt?.[field]
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-4" />
        <p className="text-slate-500">Kontakt wird geladen...</p>
      </div>
    )
  }

  if (error || !kontakt) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Users className="h-16 w-16 text-red-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Fehler</h3>
        <p className="text-slate-500 text-center max-w-md mb-4">{error || 'Kontakt nicht gefunden'}</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors"
        >
          Zurück zu Kontakte
        </button>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <KontaktHeader
        kontakt={kontakt}
        hasChanges={hasChanges}
        isSaving={isSaving}
        isDeleting={isDeleting}
        onSave={handleSave}
        onDelete={handleDelete}
        onBack={handleBack}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="stammdaten" className="gap-2">
            {kontakt.kontakt_type === 'person' ? (
              <User className="h-4 w-4" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            Stammdaten
          </TabsTrigger>
          <TabsTrigger value="kontaktdaten" className="gap-2">
            <Phone className="h-4 w-4" />
            Kontaktdaten
          </TabsTrigger>
          <TabsTrigger value="akten" className="gap-2">
            <FileText className="h-4 w-4" />
            Akten
          </TabsTrigger>
          {kontakt.kontakt_type === 'person' && (
            <TabsTrigger value="versicherungen" className="gap-2">
              <Shield className="h-4 w-4" />
              Versicherungen
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stammdaten">
          <StammdatenTab
            kontakt={kontakt}
            allTags={allTags}
            getValue={getValue}
            onChange={handleFieldChange}
          />
        </TabsContent>

        <TabsContent value="kontaktdaten">
          <KontaktdatenTab
            kontakt={kontakt}
            onRefresh={fetchKontakt}
          />
        </TabsContent>

        <TabsContent value="akten">
          <AktenTab kontaktId={kontaktId} />
        </TabsContent>

        {kontakt.kontakt_type === 'person' && (
          <TabsContent value="versicherungen">
            <VersicherungenTab
              kontakt={kontakt}
              onRefresh={fetchKontakt}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
